import { LoadingSpinner } from '@ui/LoadingSpinner';
import { queryParamsToDynamicQueryLanguageParams } from '@sonar-software/sonar-ts-lib';
import { useQueryResource } from '@hooks';
import { v3FieldSortOptions } from './FieldSortOptions';

import {
	ApiCodeToggleQueryParams,
	ReportComponentTypeFilterQueryParams,
	SearchFilterQueryParams,
	SortByFilterQueryParams
} from '../filters';
import {
	DynamicQueryOperator,
	KeyPaginationQueryParams
} from '@sonar-software/sonar-ts-types';
import { FieldGroup, PeriscopeFieldGroup, SalesforceFieldSearch } from '.';
import { keyPaginationDefaultQueryParams, keyPaginationFallbackQueryParams } from '@ui/KeyPagination';
import React, { useEffect, useRef, useState } from 'react';

type Resource = unknown[];
export type SearchablePeriscopeFieldGroupListV3QueryParams = ApiCodeToggleQueryParams &
	ReportComponentTypeFilterQueryParams &
	KeyPaginationQueryParams &
	SortByFilterQueryParams &
	SearchFilterQueryParams;

const dqlTransform = (params: SearchablePeriscopeFieldGroupListV3QueryParams) =>
	queryParamsToDynamicQueryLanguageParams(params, {
		capitalizeOrderingParams: false,
		filterParams: {
			filterByTags: { name: 'tagIds' },
			query: {
				name: params.useInternal ? 'InternalName' : 'DisplayName',
				operator: DynamicQueryOperator.Like
			}
		},
		orderingParams: {
			name: params.useInternal ? 'InternalName' : 'DisplayName'
		}
	});

export interface SearchablePeriscopeFieldGroupListV3Props {
	mapGroup?: (record: unknown) => FieldGroup;
	parentIndex: number;
	showParents?: boolean;
	uri: string;
	useDql?: boolean;
}

export const SearchablePeriscopeFieldGroupListV3: React.FC<SearchablePeriscopeFieldGroupListV3Props> = ({
	mapGroup,
	uri,
	useDql = false,
	...rest
}) => {
	const [ transformedGroups, setTransformedGroups ] = useState<FieldGroup[]>([]);
	const scrollableListRef = useRef<HTMLDivElement>(null);

	const { loading, resource, queryParams, updateParams } = useQueryResource<
		Resource,
		SearchablePeriscopeFieldGroupListV3QueryParams
	>({
		defaultParams: {
			...keyPaginationDefaultQueryParams,
			sortBy: v3FieldSortOptions[ 1 ].key,
			sortDirection: v3FieldSortOptions[ 1 ].direction
		},
		fallbackParams: keyPaginationFallbackQueryParams,
		...useDql ? { apiTransform: dqlTransform } : {},
		uri
	});

	useEffect(() => {
		if (resource) {
			setTransformedGroups(mapGroup ? resource?.map(mapGroup) : (resource as FieldGroup[]));
		}
	}, [ mapGroup, resource ]);

	return (
		<>
			<SalesforceFieldSearch
				onChange={ updateParams }
				queryParams={ queryParams }
				showInternalNameToggle={ useDql } />
			<div className='searchable-periscope-field-group-list'>
				<LoadingSpinner spinning={ loading }>
					<div ref={ scrollableListRef }>
						{ transformedGroups.map((group) =>
							<PeriscopeFieldGroup
								group={ group }
								key={ `${group.name}` }
								useApiNames={ Boolean(queryParams.useInternal) }
								{ ...rest } />) }
					</div>
				</LoadingSpinner>
			</div>
		</>
	);
};
